import { Chip, MenuItem, MenuList, Paper, Popper, Typography } from '@mui/material'
import Downshift, {
  ControllerStateAndHelpers,
  GetItemPropsOptions,
  GetMenuPropsOptions,
  GetPropsCommonOptions,
} from 'downshift'
import { connect, FormikContextType, getIn } from 'formik'
import * as _ from 'lodash'
import { MuiChipsInput } from 'mui-chips-input'
import * as React from 'react'

type GetMenuPropsFn = (
  options?: GetMenuPropsOptions,
  otherOptions?: GetPropsCommonOptions
) => any

type GetItemPropsFn = (options: GetItemPropsOptions<any>) => any

const getItemsDebounceTimeout = 500

const downshiftResetState = {
  isOpen: false,
}

interface IFormikProps {
  formik: FormikContextType<any>
}

interface IFreeTypeAheadFieldProps<T> {
  createNew: (newItem: string) => T
  disabled?: boolean
  fullWidth?: boolean
  getName: (item: T) => string
  getValue: (item: T) => string
  getItems: (filter: string) => void
  getChipColor?: (item: T) => string
  items: T[]
  name: string
  label: string
  required?: boolean
  outlined?: boolean
  readonly?: boolean
}

interface IFreeTypeAheadFieldState {
  downshiftInputValue: string
}

class FreeTypeAheadField<T> extends React.Component<
  IFreeTypeAheadFieldProps<T> & IFormikProps,
  IFreeTypeAheadFieldState
> {
  private inputRef: HTMLDivElement | null = null
  private debouncedGetItems: _.DebouncedFunc<(filter: string) => void>

  constructor(props: IFreeTypeAheadFieldProps<T> & IFormikProps) {
    super(props)
    this.state = {
      downshiftInputValue: '',
    }
    this.debouncedGetItems = _.debounce((filter: string) => {
      if (this.props.getItems) {
        this.props.getItems(filter)
      }
    }, getItemsDebounceTimeout)
  }

  private get currentFormValues() {
    const { formik, name } = this.props
    return getIn(formik.values, name, [])
  }

  private setInputRef = (element: HTMLDivElement | null) => {
    this.inputRef = element
  }

  public render() {
    const {
      fullWidth,
      label,
      required,
      formik,
      name,
      outlined,
      disabled = false,
    } = this.props

    const errorMessage = getIn(formik.errors, name)
    const hasError = errorMessage !== undefined
    const variant = outlined ? 'outlined' : 'standard'
    return (
      <Downshift
        inputValue={this.state.downshiftInputValue}
        onChange={this.onExistingSelected}
        itemToString={this.itemToString}
        onInputValueChange={this.onInputChanged}
        onOuterClick={this.onOuterClick}
        selectedItem={this.currentFormValues}
      >
        {({
          getInputProps,
          getMenuProps,
          getItemProps,
          highlightedIndex,
          inputValue,
          isOpen,
          setState,
        }) => {
          const inputProps = getInputProps()
          const onKeyDown = inputProps.onKeyDown as
            | React.KeyboardEventHandler<HTMLDivElement>
            | undefined
          const {
            ref,
            onKeyDown: _onKeyDown,
            inputValue: _inputValue,
            ...filteredInputProps
          } = inputProps as any

          return (
            <div>
              <MuiChipsInput
                {...filteredInputProps}
                InputLabelProps={{
                  required,
                  shrink: !!inputValue || this.currentFormValues.length > 0,
                }}
                label={label}
                fullWidth={fullWidth}
                disabled={disabled}
                value={this.currentFormValues.map(this.props.getName)}
                onChange={(newChips) => {
                  const { getName, createNew, formik, name } = this.props
                  const existingItems = this.currentFormValues

                  // Handle deletions
                  if (newChips.length < existingItems.length) {
                    const deletedChip = existingItems.find(
                      (item: T) => !newChips.includes(getName(item))
                    )
                    if (deletedChip) {
                      const newValues = existingItems.filter(
                        (item: T) => getName(item) !== getName(deletedChip)
                      )
                      formik.setFieldValue(name, newValues)
                    }
                  }
                  // Handle additions
                  else if (newChips.length > existingItems.length) {
                    const addedChip = newChips.find(
                      (chip) => !existingItems.some((item: T) => getName(item) === chip)
                    )
                    if (addedChip) {
                      const existingItem = this.props.items.find(
                        (item: T) => getName(item) === addedChip
                      )
                      const newItem = existingItem || createNew(addedChip)
                      if (newItem) {
                        const newValues = existingItems.concat([newItem])
                        formik.setFieldValue(name, newValues)
                      }
                    }
                  }

                  setState(downshiftResetState)
                  this.setState({ downshiftInputValue: '' })
                }}
                onInputChange={(newValue) => {
                  if (typeof newValue === 'string') {
                    this.setState({ downshiftInputValue: newValue })
                    this.debouncedGetItems(newValue)
                  }
                }}
                renderChip={(chipValue, _) => {
                  const chip = this.currentFormValues.find(
                    (item: T) => this.props.getName(item) === chipValue
                  )
                  if (!chip) return null

                  const { getName, getValue, getChipColor } = this.props
                  let chipStyle: React.CSSProperties = {
                    backgroundColor: '#FFFFFF',
                    color: '#000000',
                  }
                  if (getChipColor) {
                    const tagColor = getChipColor(chip)
                    chipStyle = { backgroundColor: tagColor, color: '#FFFFFF' }
                  }

                  return (
                    <Chip
                      sx={{ marginRight: 0.5 }}
                      key={getValue(chip)}
                      label={getName(chip)}
                      style={chipStyle}
                      variant="outlined"
                    />
                  )
                }}
                onKeyDown={onKeyDown}
                inputRef={this.setInputRef}
                helperText={errorMessage}
                error={hasError}
                variant={variant}
              />
              {this.renderSuggestions(
                getMenuProps,
                getItemProps,
                isOpen,
                highlightedIndex
              )}
            </div>
          )
        }}
      </Downshift>
    )
  }

  public itemToString = (item: T) => {
    return this.props.getName(item)
  }

  private onExistingSelected = (
    selectedItem: T,
    helpers: ControllerStateAndHelpers<T>
  ) => {
    if (selectedItem) {
      this.addChip(selectedItem)
    }
    helpers.setState(downshiftResetState)
    this.setState({ downshiftInputValue: '' })
  }

  private onOuterClick = (helpers: ControllerStateAndHelpers<T>) => {
    helpers.setState(downshiftResetState)
    this.setState({ downshiftInputValue: '' })
  }

  private addChip = (itemToAdd: T) => {
    const { formik, name } = this.props
    const newValues = this.currentFormValues.concat([itemToAdd])
    formik.setFieldValue(name, newValues)
  }

  private onInputChanged = (inputValue: string) => {
    if (typeof inputValue === 'string') {
      this.debouncedGetItems(inputValue)
    }
  }

  private filterSelectedFromOptions = () => {
    const { getValue } = this.props
    const chipValues: string[] = this.currentFormValues.map(getValue)
    return (item: T) => {
      return !chipValues.some((cv) => getValue(item) === cv)
    }
  }

  private renderSuggestions(
    getMenuProps: GetMenuPropsFn,
    getItemProps: GetItemPropsFn,
    isOpen: boolean,
    highlightedIndex: number | null
  ) {
    const { items = [] } = this.props
    const menuProps = getMenuProps({}, { suppressRefError: true })
    const menuItems = items
      .filter(this.filterSelectedFromOptions())
      .map(this.renderMenuItem(getItemProps, highlightedIndex))

    const noSuggestionsMessage = (
      <Typography sx={{ fontStyle: 'italic', padding: '11px 16px' }} variant="body2">
        No suggestions found. Press Enter to create a new entry
      </Typography>
    )

    const suggestions = <MenuList {...menuProps}>{menuItems}</MenuList>
    return (
      <div>
        <Popper
          sx={{ zIndex: 9999 }}
          open={isOpen}
          keepMounted={false}
          anchorEl={this.inputRef}
          placement="bottom"
          modifiers={[
            {
              name: 'preventOverflow',
              options: {
                boundary: 'viewport',
              },
            },
          ]}
          style={{
            width: this.inputRef ? this.inputRef.clientWidth : undefined,
          }}
        >
          <Paper>{menuItems.length > 0 ? suggestions : noSuggestionsMessage}</Paper>
        </Popper>
      </div>
    )
  }

  private renderMenuItem =
    (getItemProps: GetItemPropsFn, highlightedIndex: number | null) =>
    (item: T, index: number) => {
      const { getName, getValue, getChipColor } = this.props
      const itemProps = getItemProps({ key: getValue(item), item })
      const isSelected = highlightedIndex === index
      let itemStyle: React.CSSProperties = {
        backgroundColor: '#FFFFFF',
        color: '#000000',
      }
      if (getChipColor) {
        const itemColor = getChipColor(item)
        itemStyle = { backgroundColor: itemColor, color: '#FFFFFF' }
      }
      return (
        <MenuItem {...itemProps} selected={isSelected} style={itemStyle}>
          {getName(item)}
        </MenuItem>
      )
    }
}

export default connect<IFreeTypeAheadFieldProps<any>, any>(FreeTypeAheadField)
